import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

const Coupons = () => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);

  const getCoupons = async () => {
    try {
      setLoading(true);

      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API.BASE_URL}coupons?page=1&limit=20`,
        { headers }
      );

      // ✅ your response: { status, data: { title, message, coupons: [] } }
      const list = res?.data?.data?.coupons;
      setCoupons(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch offers", {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
      });
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCouponBadge = (c) => {
    if (c?.isBogoCoupon) return { label: "BOGO", color: "secondary" };
    if (c?.isFreebieCoupon) return { label: "FREEBIE", color: "success" };
    if (c?.isCashbackCoupon) return { label: "CASHBACK", color: "info" };
    if (c?.isMrpDiscount) return { label: "MRP DISCOUNT", color: "warning" };
    if (c?.discountPrice != null) return { label: `₹${c.discountPrice}`, color: "primary" };
    if (c?.discountPercent != null) return { label: `${c.discountPercent}% OFF`, color: "primary" };
    if (c?.discountAmount != null) return { label: `₹${c.discountAmount} OFF`, color: "primary" };
    return { label: "OFFER", color: "default" };
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Coupons
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loading ? "Loading..." : `${coupons.length} found`}
        </Typography>
      </Stack>

      {loading ? (
        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : coupons.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            No coupons available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try again later.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {coupons.map((c) => {
            const badge = getCouponBadge(c);

            const categoryNames = Array.isArray(c?.applicableCategories)
              ? c.applicableCategories.map((x) => x?.name).filter(Boolean)
              : [];

            const productNames = Array.isArray(c?.applicableProducts)
              ? c.applicableProducts.map((x) => x?.name).filter(Boolean)
              : [];

            const excludedOutletCount = Array.isArray(c?.excludedOutlets) ? c.excludedOutlets.length : 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={c?._id || c?.couponCode}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                          {c?.title || "Coupon"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Code: <b>{c?.couponCode || "—"}</b>
                        </Typography>
                      </Box>

                      <Chip size="small" label={badge.label} color={badge.color} />
                    </Stack>

                    {c?.description ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {c.description}
                      </Typography>
                    ) : null}

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={0.75}>
                      <Typography variant="body2" color="text.secondary">
                        Usage: <b>{c?.usageType || "—"}</b>
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Min order: <b>{c?.minOrderAmount != null ? `₹${c.minOrderAmount}` : "—"}</b>
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Validity:{" "}
                        <b>
                          {formatDate(c?.validityStartDate)} - {formatDate(c?.validityEndDate)}
                        </b>
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Order type: <b>{c?.orderType || "ALL"}</b>
                      </Typography>

                      {excludedOutletCount > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Excluded outlets: <b>{excludedOutletCount}</b>
                        </Typography>
                      ) : null}

                      {categoryNames.length > 0 ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Categories
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                            {categoryNames.slice(0, 6).map((name) => (
                              <Chip key={name} size="small" label={name} variant="outlined" />
                            ))}
                            {categoryNames.length > 6 ? (
                              <Chip size="small" label={`+${categoryNames.length - 6}`} variant="outlined" />
                            ) : null}
                          </Stack>
                        </Box>
                      ) : null}

                      {productNames.length > 0 ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Products
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                            {productNames.slice(0, 4).map((name) => (
                              <Chip key={name} size="small" label={name} variant="outlined" />
                            ))}
                            {productNames.length > 4 ? (
                              <Chip size="small" label={`+${productNames.length - 4}`} variant="outlined" />
                            ) : null}
                          </Stack>
                        </Box>
                      ) : null}

                      {Array.isArray(c?.termsAndConditions) && c.termsAndConditions.length > 0 ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Terms
                          </Typography>
                          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                            {c.termsAndConditions.slice(0, 3).map((t, idx) => (
                              <li key={idx}>
                                <Typography variant="body2">{t}</Typography>
                              </li>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Coupons;